import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Upload } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ReferencesInput } from "@/components/profile-creation/ReferencesInput";
import { PhotoUploadError, uploadProfilePhoto } from "@/lib/photoUpload";
import { useSignedPhotoUrl } from "@/lib/photoUrl";
import {
  REQUIRED_REFERENCE_COUNT,
  isValidReference,
  type ProfileReference,
  type RequiredFieldSpec,
} from "@/lib/requiredFields";
import {
  formatPhoneInput,
  isPlausiblePhone,
  normalizePhone,
  PHONE_ERROR_MESSAGE,
} from "@/lib/phone";

/**
 * Image-upload control for manifest entries whose input kind is "photo".
 * Uses the same uploader as the signup wizard (@/lib/photoUpload) and stores
 * the returned storage PATH as the field's value.
 */
function PhotoField({
  id,
  value,
  onChange,
  disabled,
}: {
  id: string;
  value: string;
  onChange: (path: string) => void;
  disabled: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const signedUrl = useSignedPhotoUrl(value || undefined);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      onChange(await uploadProfilePhoto(file));
      toast.success("Photo uploaded");
    } catch (err) {
      console.error("profile_backfill.photo_upload_failed");
      toast.error(
        err instanceof PhotoUploadError
          ? err.message
          : "Failed to upload photo. Please try again.",
      );
    } finally {
      setUploading(false);
      if (event.target) event.target.value = "";
    }
  };

  return (
    <div className="flex items-center gap-4">
      <Avatar className="h-20 w-20">
        <AvatarImage src={signedUrl ?? undefined} alt="Profile" />
        <AvatarFallback>
          <Upload className="h-5 w-5 text-muted-foreground" />
        </AvatarFallback>
      </Avatar>
      <div>
        <input
          type="file"
          accept="image/*"
          id={id}
          className="hidden"
          onChange={handleUpload}
          disabled={disabled || uploading}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => document.getElementById(id)?.click()}
          disabled={disabled || uploading}
        >
          {uploading ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Uploading…
            </>
          ) : (
            <>
              <Upload className="mr-2 h-4 w-4" />
              {value ? "Change" : "Upload"}
            </>
          )}
        </Button>
        <p className="mt-2 text-xs text-muted-foreground">
          Maximum file size: 5MB. JPG, PNG or WEBP.
        </p>
      </div>
    </div>
  );
}

interface Props {
  open: boolean;
  /** The manifest entries this member is missing. Rendered focused — nothing else. */
  missing: RequiredFieldSpec[];
  profileId: string;
  onOpenChange: (open: boolean) => void;
  /** Called after a successful save so the parent can re-check / dismiss. */
  onCompleted: () => void;
}

/**
 * Manifest-driven focused fill-in. Renders ONLY the required fields the member
 * is missing (passed in via `missing`) — not the 6-step wizard, not a lockout.
 * Persisting maps each field back to its location (profiles / contact_info /
 * profile_references) using the manifest spec.
 */
export function ProfileBackfillDialog({
  open,
  missing,
  profileId,
  onOpenChange,
  onCompleted,
}: Props) {
  const [saving, setSaving] = useState(false);
  // Generic string values keyed by field key (for text/textarea/tel inputs).
  const [values, setValues] = useState<Record<string, string>>({});
  // References handled separately (array field).
  const [references, setReferences] = useState<ProfileReference[]>([
    { name: "", relationship: "", phone: "", email: "" },
    { name: "", relationship: "", phone: "", email: "" },
  ]);
  const [error, setError] = useState<string | null>(null);

  const setVal = (key: string, v: string) =>
    setValues((prev) => ({ ...prev, [key]: v }));

  const validate = (): string | null => {
    for (const spec of missing) {
      if (spec.input === "references") {
        if (references.filter(isValidReference).length < REQUIRED_REFERENCE_COUNT) {
          return `Please provide ${REQUIRED_REFERENCE_COUNT} complete references.`;
        }
      } else if (spec.input === "photo") {
        // Value is the storage path returned by the upload — must be set.
        if (!values[spec.key]?.trim()) {
          return `Please upload a photo for: ${spec.label}.`;
        }
      } else if (!values[spec.key]?.trim()) {
        return `Please fill in: ${spec.label}.`;
      } else if (spec.input === "tel" && !isPlausiblePhone(values[spec.key])) {
        return PHONE_ERROR_MESSAGE;
      }
    }
    return null;
  };

  const handleSave = async () => {
    const v = validate();
    if (v) {
      setError(v);
      return;
    }
    setError(null);
    setSaving(true);
    try {
      // Group simple-column writes by table.
      const profilePatch: Record<string, string> = { id: profileId };
      const contactPatch: Record<string, string> = { profile_id: profileId };
      let writeProfile = false;
      let writeContact = false;
      let writeReferences = false;

      for (const spec of missing) {
        if (spec.input === "references") {
          writeReferences = true;
          continue;
        }
        // Phones are stored normalized (E.164 where parseable) — see @/lib/phone.
        const raw = values[spec.key]?.trim() ?? "";
        const val = spec.input === "tel" ? normalizePhone(raw) ?? raw : raw;
        if (spec.location === "profiles" && spec.column) {
          profilePatch[spec.column] = val;
          writeProfile = true;
        } else if (spec.location === "contact_info" && spec.column) {
          contactPatch[spec.column] = val;
          writeContact = true;
        }
      }

      if (writeProfile) {
        const { error: e } = await supabase
          .from("profiles")
          .update(profilePatch)
          .eq("id", profileId);
        if (e) throw e;
      }

      if (writeContact) {
        const { error: e } = await supabase
          .from("contact_info")
          .upsert(contactPatch, { onConflict: "profile_id" });
        if (e) throw e;
      }

      if (writeReferences) {
        const validRefs = references.filter(isValidReference);
        await supabase
          .from("profile_references")
          .delete()
          .eq("profile_id", profileId);
        const { error: e } = await supabase.from("profile_references").insert(
          validRefs.map((r) => ({
            profile_id: profileId,
            name: r.name.trim(),
            relationship: r.relationship.trim(),
            phone: normalizePhone(r.phone),
            email: r.email?.trim() || null,
          })),
        );
        if (e) throw e;
      }

      toast.success("Thanks — your profile is up to date.");
      onCompleted();
      onOpenChange(false);
    } catch (err) {
      console.error("profile_backfill.save_failed");
      setError(
        err instanceof Error ? err.message : "Failed to save. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>A few more details</DialogTitle>
          <DialogDescription>
            We've added a few things to make introductions the traditional way.
            Please complete the items below — it only takes a minute.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-2">
          {missing.map((spec) => {
            if (spec.input === "references") {
              return (
                <div key={spec.key} className="space-y-3">
                  <div>
                    <Label className="text-base">{spec.label}</Label>
                    {spec.helper && (
                      <p className="text-sm text-muted-foreground">{spec.helper}</p>
                    )}
                  </div>
                  <ReferencesInput value={references} onChange={setReferences} />
                </div>
              );
            }
            return (
              <div key={spec.key} className="space-y-2">
                <Label htmlFor={`backfill-${spec.key}`}>{spec.label}</Label>
                {spec.helper && (
                  <p className="text-sm text-muted-foreground">{spec.helper}</p>
                )}
                {spec.input === "photo" ? (
                  <PhotoField
                    id={`backfill-${spec.key}`}
                    value={values[spec.key] ?? ""}
                    onChange={(path) => setVal(spec.key, path)}
                    disabled={saving}
                  />
                ) : spec.input === "textarea" ? (
                  <Textarea
                    id={`backfill-${spec.key}`}
                    value={values[spec.key] ?? ""}
                    onChange={(e) => setVal(spec.key, e.target.value)}
                    className="min-h-[100px]"
                  />
                ) : (
                  <Input
                    id={`backfill-${spec.key}`}
                    type={spec.input === "tel" ? "tel" : "text"}
                    value={values[spec.key] ?? ""}
                    placeholder={spec.input === "tel" ? "(555) 123-4567" : undefined}
                    onChange={(e) =>
                      setVal(
                        spec.key,
                        spec.input === "tel"
                          ? formatPhoneInput(e.target.value)
                          : e.target.value,
                      )
                    }
                  />
                )}
              </div>
            );
          })}

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button onClick={handleSave} disabled={saving} className="min-w-32">
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving…
              </>
            ) : (
              "Save"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
